import { useCallback, useContext, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { SeatSaverListingWizard } from 'src/modals/SeatSaverListingWizard/SeatSaverListingWizard';
import { SeatSaverListingWizardProps } from 'src/modals/SeatSaverListingWizard/SeatSaverListingWizard.types';
import { SeatSaverIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Feature, Permission } from 'src/WebApiController';

import * as styles from './EventActions.css';

export const LaunchAddSeatSaver = ({
  event,
}: SeatSaverListingWizardProps & {
  event: Event;
}) => {
  const { isDarkMode } = useSiteTheme();
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_AddSeatSaver
  );
  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);
  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);

  const { setModal } = useContext(ModalContext);
  const {
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  const addSeatSaver = useCallback(() => {
    setModal({
      children: <SeatSaverListingWizard events={[event]} />,
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [setModal, event]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setListItemExpansion(true, [event.virtId]);

      addSeatSaver();
    },
    [setListItemExpansion, event.virtId, addSeatSaver]
  );

  const iconColor = useMemo(() => {
    if (isHovering) {
      return vars.color.iconHover;
    }
    return vars.color.textPrimary;
  }, [isHovering]);

  if (!hasPermission || !hasSeatSaverFeature) {
    return null;
  }

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <Stack
          ref={elementRef}
          onClick={onLaunchDialog}
          className={styles.eventActionsGroupItem}
        >
          <SeatSaverIcon
            withHoverEffect
            fill={iconColor}
            size={vars.iconSize.l}
          />
        </Stack>
      }
    >
      <Content id={ContentId.AddPlaceholder} />
    </Tooltip>
  );
};
